"use client";

import React, { useEffect, useState } from 'react';
import { formatLiveStatNumber } from './LiveStat';

/**
 * The live assets-under-guidance counter, displayed on blog and article pages
 * in the same visual treatment as /pricing. DEL-562 removed the animated
 * LiveStat component here because its 180s slot-machine animation produced
 * different intermediate values depending on when the user happened to look -
 * the trust defect surfaced in DEL-560 dogfood. We render the live API value
 * directly via the non-animated `formatLiveStatNumber` primitive instead, so
 * the displayed number is invariant under any UI interaction.
 *
 * Placeholder contract (DEL-563, see DESIGN.md "Unavailable data"): if the
 * API value is missing or non-positive, the widget renders nothing rather
 * than falling back to a made-up number. Same contract as the other 4 sites
 * (HomePageClient, Pricing, LoginClient, TrustSignals).
 */
export default function AssetsUnderGuidance() {
  const [totalAssets, setTotalAssets] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch('/api/public-stats/stats')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.total_assets_under_guidance > 0) {
          setTotalAssets(data.total_assets_under_guidance);
        }
      })
      .catch(() => {
        // Same-origin public endpoint with no auth; a failure just means the
        // widget renders nothing, not that the page breaks.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (totalAssets === null || totalAssets <= 0) {
    return null;
  }

  return (
    <div className="border-y-2 border-gray-200 bg-white py-6 my-8">
      <div className="max-w-4xl mx-auto px-4 flex flex-col items-center gap-2">
        <div role="status" aria-live="polite" aria-label="Assets under guidance">
          <p className="text-3xl md:text-4xl font-bold font-mono text-positive-600">
            {formatLiveStatNumber(totalAssets, "currency")}
          </p>
          <p className="text-sm text-gray-500">Assets under guidance</p>
        </div>
      </div>
    </div>
  );
}
